/**
 * OrderTrackingPage — /order/:orderId
 *
 * Live order status after checkout, so customers never order blind. Polls the
 * public track endpoint (the unguessable order id is the access token) every
 * 12 seconds and renders a step timeline:
 *   Pending → Confirmed → Preparing → Ready → Delivered   (or Cancelled)
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { resolveMenuTheme, menuCssVars } from '../theme/menuTheme';
import { restaurantApi } from '../api/apiClient';
import { formatPrice } from '../utils/currency';

const STEPS = ['Pending', 'Confirmed', 'Preparing', 'Ready', 'Delivered'];
const STEP_ICONS = { Pending: '🕐', Confirmed: '✅', Preparing: '👨‍🍳', Ready: '🛎️', Delivered: '🎉', Cancelled: '✕' };

export default function OrderTrackingPage({ restaurant }) {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tu } = useLanguage();
  const branchQ = searchParams.get('branch') ? `?branch=${searchParams.get('branch')}` : '';

  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);
  const { theme: menuTheme, colors: menuColors } = resolveMenuTheme(restaurant);

  useEffect(() => {
    let alive = true;
    const load = () => restaurantApi.trackOrder(restaurant.slug, orderId)
      .then(r => { if (alive) { setOrder(r.data); setError(false); } })
      .catch(() => { if (alive && !order) setError(true); });
    load();
    const id = setInterval(load, 12000);                  // live updates
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line
  }, [orderId, restaurant.slug]);

  const statusKey = (s) => tu('status_' + String(s || '').toLowerCase()) || s;

  if (error) {
    return (
      <div className={`track-page theme-${menuTheme}`} style={menuCssVars(menuColors)}>
        <div className="track-wrap">
          <div className="track-icon-big">🔍</div>
          <h2 className="track-h2">{tu('track_not_found')}</h2>
          <button className="track-back" onClick={() => navigate(`/menu${branchQ}`)}>{tu('cart_back')}</button>
        </div>
        <Style />
      </div>
    );
  }

  const cancelled = order?.status === 'Cancelled';
  const stepIndex = order ? STEPS.indexOf(order.status) : -1;

  return (
    <div className={`track-page theme-${menuTheme}`} style={menuCssVars(menuColors)}>
      <div className="track-wrap">
        <button className="track-back-top" onClick={() => navigate(`/menu${branchQ}`)}>{tu('cart_back')}</button>
        <h1 className="track-h1">{tu('track_title')}</h1>

        {!order ? (
          <div className="track-loading"><span className="track-spin">🍽️</span></div>
        ) : (
          <>
            <div className="track-card">
              <div className="track-head-row">
                <span className="track-no">{tu('track_order_no')} #{order.orderNumber || String(order._id).slice(-6).toUpperCase()}</span>
                <span className={`track-badge ${cancelled ? 'cancelled' : ''}`}>
                  {STEP_ICONS[order.status] || '•'} {statusKey(order.status)}
                </span>
              </div>

              {!cancelled && (
                <div className="track-steps">
                  {STEPS.map((s, i) => (
                    <div key={s} className={`track-step ${i <= stepIndex ? 'done' : ''} ${i === stepIndex ? 'now' : ''}`}>
                      <div className="track-dot">{i <= stepIndex ? STEP_ICONS[s] : ''}</div>
                      <div className="track-step-label">{statusKey(s)}</div>
                      {i < STEPS.length - 1 && <div className={`track-line ${i < stepIndex ? 'done' : ''}`} />}
                    </div>
                  ))}
                </div>
              )}

              <div className="track-meta">
                {order.branchName && <div className="track-meta-row"><span>📍</span><span>{order.branchName}</span></div>}
                <div className="track-meta-row"><span>{tu('track_placed_at')}:</span><span dir="ltr">{new Date(order.createdAt).toLocaleString()}</span></div>
              </div>
            </div>

            <div className="track-card">
              <div className="track-sec-title">{tu('track_items')}</div>
              {(order.items || []).map((it, i) => (
                <div key={i} className="track-item">
                  <span className="track-item-name">{it.quantity}× {it.productName}</span>
                  <span className="track-item-price">{formatPrice(it.subtotal, restaurant.currencyCode)}</span>
                </div>
              ))}
              <div className="track-total-row">
                <span>{tu('track_total')}</span>
                <span>{formatPrice(order.total, restaurant.currencyCode)}</span>
              </div>
            </div>

            <p className="track-auto">↻ {tu('track_auto')}</p>
          </>
        )}
      </div>
      <Style />
    </div>
  );
}

function Style() {
  return (
    <style>{`
      .track-page { min-height:100vh; background:var(--bg); color:var(--text); font-family:'DM Sans',sans-serif; }
      .track-wrap { max-width:560px; margin:0 auto; padding:1.5rem 1.25rem 3rem; }
      .track-back-top { background:none; border:1px solid var(--border); color:var(--muted); border-radius:10px; padding:.5rem 1rem; cursor:pointer; font-family:'DM Sans',sans-serif; font-size:.85rem; margin-bottom:1.25rem; }
      .track-h1 { font-family:'Playfair Display',serif; font-size:1.9rem; margin-bottom:1.25rem; color:var(--text); }
      .track-h2 { font-family:'Playfair Display',serif; color:var(--text); margin:1rem 0; }
      .track-loading { display:flex; justify-content:center; padding:4rem 0; }
      .track-spin { font-size:3rem; animation:trackSpin 1.2s linear infinite; display:inline-block; }
      @keyframes trackSpin { to { transform:rotate(360deg); } }
      .track-card { background:var(--surface); border:1px solid var(--border); border-radius:18px; padding:1.4rem; margin-bottom:1rem; }
      .track-head-row { display:flex; align-items:center; justify-content:space-between; gap:.75rem; flex-wrap:wrap; margin-bottom:1.2rem; }
      .track-no { font-weight:800; font-size:1.05rem; }
      .track-badge { padding:.4rem .9rem; border-radius:20px; background:var(--accent-soft); color:var(--accent); font-size:.82rem; font-weight:700; }
      .track-badge.cancelled { background:rgba(239,68,68,.15); color:#f87171; }
      .track-steps { display:flex; justify-content:space-between; position:relative; margin:1.25rem 0 1.5rem; }
      .track-step { display:flex; flex-direction:column; align-items:center; flex:1; position:relative; }
      .track-dot { width:34px; height:34px; border-radius:50%; border:2px solid var(--border); background:var(--surface-2); display:flex; align-items:center; justify-content:center; font-size:.85rem; transition:all .3s; z-index:1; }
      .track-step.done .track-dot { border-color:var(--accent); background:var(--accent-soft); }
      .track-step.now .track-dot { box-shadow:0 0 0 5px var(--accent-soft); }
      .track-step-label { font-size:.62rem; color:var(--muted); margin-top:.45rem; text-align:center; }
      .track-step.now .track-step-label { color:var(--accent); font-weight:700; }
      .track-line { position:absolute; top:17px; left:50%; width:100%; height:2px; background:var(--border); z-index:0; }
      .track-line.done { background:var(--accent); }
      [dir="rtl"] .track-line { left:auto; right:50%; }
      .track-meta { border-top:1px solid var(--border); padding-top:.9rem; display:flex; flex-direction:column; gap:.4rem; }
      .track-meta-row { display:flex; gap:.5rem; font-size:.82rem; color:var(--muted); }
      .track-sec-title { font-size:.72rem; font-weight:800; text-transform:uppercase; letter-spacing:.08em; color:var(--faint); margin-bottom:.75rem; }
      .track-item { display:flex; justify-content:space-between; gap:.75rem; padding:.45rem 0; font-size:.9rem; border-bottom:1px solid var(--border); }
      .track-item:last-of-type { border-bottom:none; }
      .track-item-price { color:var(--muted); white-space:nowrap; }
      .track-total-row { display:flex; justify-content:space-between; padding-top:.8rem; margin-top:.4rem; border-top:1px solid var(--border); font-weight:800; color:var(--accent); }
      .track-auto { text-align:center; font-size:.72rem; color:var(--faint); }
      .track-icon-big { font-size:3.5rem; text-align:center; margin-top:4rem; }
      .track-back { display:block; margin:0 auto; padding:.7rem 1.6rem; background:var(--accent); color:var(--accent-text); border:none; border-radius:10px; cursor:pointer; font-family:'DM Sans',sans-serif; font-weight:700; }
    `}</style>
  );
}
